import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { usersDB } from '../lib/db/users';
import { trainingsDB } from '../lib/db/trainings';
import { trackEvent, EVENTS } from '../lib/analytics';
import { formatDisplayTime } from '../lib/timeUtils';
import { startOfWeek, addDays, format, parseISO, startOfMonth, subDays, subWeeks, addWeeks, endOfWeek, isAfter } from 'date-fns';
import SessionDetailsModal from '../components/training/SessionDetailsModal';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Progress() {
  const [userData, setUserData] = useState(null);
  const [weekTrainings, setWeekTrainings] = useState([]);
  const [recentTrainings, setRecentTrainings] = useState([]);
  const [drillStats, setDrillStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedDrillFilter, setSelectedDrillFilter] = useState('all');
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation();
  
  const fetchData = async () => {
    const user = await usersDB.getUserData(currentUser.uid);
    setUserData(user);
    
    // Get selected week's trainings
    const weekStart = selectedWeekStart;
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
    const currentDate = new Date();
    const endDate = isAfter(weekEnd, currentDate) ? currentDate : weekEnd;
    
    const weekTrainingsData = await trainingsDB.getTrainings(currentUser.uid, {
      startDate: weekStart,
      endDate: endDate
    });
    setWeekTrainings(weekTrainingsData);
    
    // Get recent trainings (last 30 days)
    const monthStart = startOfMonth(subDays(new Date(), 30));
    const recentTrainingsData = await trainingsDB.getTrainings(currentUser.uid, {
      startDate: monthStart,
      endDate: new Date()
    });
    setRecentTrainings(recentTrainingsData);
    
    // Calculate drill statistics
    const drillStatsMap = new Map();
    recentTrainingsData.forEach(training => {
      training.drills.forEach(drill => {
        const key = drill.drillId;
        if (!drillStatsMap.has(key)) {
          drillStatsMap.set(key, {
            drillId: drill.drillId,
            drillName: drill.drillName,
            totalTime: 0,
            sessions: 0,
            lastPracticed: null
          });
        }
        const stats = drillStatsMap.get(key);
        stats.totalTime += drill.durationSeconds;
        stats.sessions += 1;
        if (!stats.lastPracticed || training.startedAt > stats.lastPracticed) {
          stats.lastPracticed = training.startedAt;
        }
      });
    });
    
    const drillStatsArray = Array.from(drillStatsMap.values())
      .sort((a, b) => b.totalTime - a.totalTime)
      .slice(0, 10); // Top 10 most practiced drills
    
    setDrillStats(drillStatsArray);
    setLoading(false);
    trackEvent(EVENTS.PROGRESS_VIEWED);
  };

  useEffect(() => {
    fetchData();
  }, [currentUser, selectedWeekStart]);

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  const handleSessionUpdate = () => {
    // Refresh data after update
    fetchData();
  };

  // Get localized day abbreviation
  const getDayAbbr = (date) => {
    const dayAbbr = format(date, 'EEE');
    return t(`common.days_abbr.${dayAbbr}`);
  };

  // Color palette for drills
  const drillColors = [
    '#4F7BFF', // Blue
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#F97316', // Orange
    '#14B8A6', // Teal
    '#6366F1', // Indigo
  ];

  // Get color for a drill by index
  const getDrillColor = (index) => {
    return drillColors[index % drillColors.length];
  };

  // Get color for a drill by ID
  const getDrillColorById = (drillId) => {
    const index = uniqueDrills.findIndex(d => d.drillId === drillId);
    return index >= 0 ? getDrillColor(index) : '#4F7BFF';
  };

  // Get unique drills from week trainings
  const uniqueDrills = useMemo(() => {
    const drillMap = new Map();
    (weekTrainings || []).forEach(training => {
      training.drills?.forEach(drill => {
        if (!drillMap.has(drill.drillId)) {
          drillMap.set(drill.drillId, {
            drillId: drill.drillId,
            drillName: drill.drillName
          });
        }
      });
    });
    return Array.from(drillMap.values());
  }, [weekTrainings]);

  // Process chart data: group by day and drill
  const chartData = useMemo(() => {
    const weekStart = selectedWeekStart;
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
    const currentDate = new Date();
    const maxDate = isAfter(weekEnd, currentDate) ? currentDate : weekEnd;
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const day = addDays(weekStart, i);
      return day;
    });
    
    // Initialize data structure for each day
    const dayDataMap = new Map();
    weekDays.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      dayDataMap.set(dateStr, {
        day: getDayAbbr(day),
        date: dateStr,
        total: 0,
        drills: {}
      });
    });

    // Process trainings and group by day and drill
    (weekTrainings || []).forEach(training => {
      const trainingDate = format(parseISO(training.startedAt), 'yyyy-MM-dd');
      const dayData = dayDataMap.get(trainingDate);
      
      if (dayData) {
        training.drills?.forEach(drill => {
          const drillId = drill.drillId;
          const minutes = Math.floor(drill.durationSeconds / 60);
          
          if (!dayData.drills[drillId]) {
            dayData.drills[drillId] = 0;
          }
          dayData.drills[drillId] += minutes;
          dayData.total += minutes;
        });
      }
    });

    // Convert to array format for chart, sorted by date
    const chartDataArray = Array.from(dayDataMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0])) // Sort by date string
      .map(([dateStr, dayData]) => {
        const result = {
          day: dayData.day,
          date: dayData.date,
          total: dayData.total
        };
        
        // Add each drill's minutes
        uniqueDrills.forEach(drill => {
          result[drill.drillId] = dayData.drills[drill.drillId] || 0;
        });
        
        return result;
      });

    return chartDataArray;
  }, [weekTrainings, uniqueDrills, t, selectedWeekStart]);

  // Filter chart data based on selected drill
  const filteredChartData = useMemo(() => {
    if (selectedDrillFilter === 'all') {
      // Return full chart data with all drills for multi-bar display
      return chartData;
    } else {
      return chartData.map(day => ({
        day: day.day,
        date: day.date,
        minutes: day[selectedDrillFilter] || 0
      }));
    }
  }, [chartData, selectedDrillFilter]);
  
  if (loading) return <div>Loading...</div>;
  
  // Create week calendar (always show current week)
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const currentWeekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  
  // Navigation handlers
  const handlePreviousWeek = () => {
    const newWeekStart = subWeeks(selectedWeekStart, 1);
    setSelectedWeekStart(newWeekStart);
  };
  
  const handleNextWeek = () => {
    const currentDate = new Date();
    const currentWeekStartDate = startOfWeek(currentDate, { weekStartsOn: 0 });
    const nextWeekStart = addWeeks(selectedWeekStart, 1);
    
    // Don't allow moving into the future - only allow if next week has already started
    if (!isAfter(nextWeekStart, currentDate)) {
      setSelectedWeekStart(nextWeekStart);
    }
  };
  
  // Format week date range (e.g., "1-7/12/2025")
  const formatWeekRange = (weekStartDate) => {
    const weekEndDate = endOfWeek(weekStartDate, { weekStartsOn: 0 });
    const startDay = format(weekStartDate, 'd');
    const endDay = format(weekEndDate, 'd');
    const month = format(weekStartDate, 'M');
    const year = format(weekStartDate, 'yyyy');
    return `${startDay}-${endDay}/${month}/${year}`;
  };
  
  // Check if we can navigate to next week
  const canNavigateNext = () => {
    const currentDate = new Date();
    const nextWeekStart = addWeeks(selectedWeekStart, 1);
    // Allow navigation if next week has already started (not entirely in the future)
    return !isAfter(nextWeekStart, currentDate);
  };
  
  const hasTrainingOnDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return (weekTrainings || []).some(t => {
      const trainingDate = format(parseISO(t.startedAt), 'yyyy-MM-dd');
      return trainingDate === dateStr;
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => window.history.back()}
            className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
            title={t('common.back')}
          >
            {i18n.language === 'he' ? '→' : '←'}
          </button>
          <h1 className="text-xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
            {t('progress.title')}
          </h1>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-6 md:mb-8 bg-white p-2 rounded-2xl shadow-lg">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-3 md:py-4 px-2 md:px-4 rounded-xl font-semibold text-sm md:text-xl transition-all min-h-[48px] ${
              activeTab === 'overview'
                ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t('progress.overview')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 md:py-4 px-2 md:px-4 rounded-xl font-semibold text-sm md:text-xl transition-all min-h-[48px] ${
              activeTab === 'history'
                ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t('progress.history')}
          </button>
          <button
            onClick={() => setActiveTab('drills')}
            className={`flex-1 py-3 md:py-4 px-2 md:px-4 rounded-xl font-semibold text-sm md:text-xl transition-all min-h-[48px] ${
              activeTab === 'drills'
                ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t('progress.drill_stats')}
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl animate-slide-up">
              {/* Streaks */}
              <div className="space-y-3 md:space-y-4">
                <div className="flex justify-between items-center p-3 md:p-4 bg-gradient-to-r from-accent-500/10 to-primary-500/10 rounded-2xl">
                  <span className="text-gray-700 text-base md:text-xl font-semibold">{t('progress.current_streak')}</span>
                  <span className="text-2xl md:text-4xl font-bold text-accent-600">
                    🔥 {userData.stats.currentStreak} {t('common.days')}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 md:p-4 bg-gradient-to-r from-primary-500/10 to-blue-500/10 rounded-2xl">
                  <span className="text-gray-700 text-base md:text-xl font-semibold">{t('progress.longest_streak')}</span>
                  <span className="text-2xl md:text-4xl font-bold text-primary-600">
                    🏆 {userData.stats.longestStreak} {t('common.days')}
                  </span>
                </div>
              </div>
              
              <div className="border-t pt-4 md:pt-6 mt-4 md:mt-6">
                <h3 className="font-bold mb-4 md:mb-6 text-lg md:text-2xl text-gray-800">{t('progress.all_time')}</h3>
                <div className="grid grid-cols-2 gap-4 md:gap-6">
                  <div className="text-center p-4 md:p-6 bg-gradient-to-br from-primary-500/10 to-primary-600/20 rounded-2xl shadow-lg card-hover">
                    <div className="text-2xl md:text-4xl font-bold text-primary-600 mb-2">{formatDisplayTime(userData.stats.totalTrainingSeconds, t)}</div>
                  </div>
                  <div className="text-center p-4 md:p-6 bg-gradient-to-br from-success-500/10 to-success-600/20 rounded-2xl shadow-lg card-hover">
                    <div className="text-2xl md:text-4xl font-bold text-success-600 mb-2">{userData.stats.totalSessions}</div>
                    <div className="text-gray-700 font-semibold text-sm md:text-base">{t('progress.total_sessions')}</div>
                  </div>
                </div>
              </div>
              
              {/* Week Calendar */}
              <div className="border-t pt-4 md:pt-6 mt-4 md:mt-6">
                <h3 className="font-bold mb-4 md:mb-6 text-lg md:text-2xl text-gray-800">{t('progress.training_calendar')}</h3>
                <div className="grid grid-cols-7 gap-2 md:gap-3">
                  {currentWeekDays.map(day => (
                    <div key={day.toString()} className="text-center p-2 md:p-3 rounded-xl bg-gray-50 card-hover">
                      <div className="text-xs md:text-sm font-semibold text-gray-600 mb-1 md:mb-2">
                        {getDayAbbr(day)}
                      </div>
                      <div className={`text-2xl md:text-3xl ${hasTrainingOnDate(day) ? '' : 'opacity-20'}`}>
                        {hasTrainingOnDate(day) ? '✅' : '⬜'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Weekly Practice Chart */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl animate-slide-up">
              <div className="flex items-center justify-between mb-4 md:mb-6" style={{ direction: 'rtl' }}>
                <h3 className="font-bold text-lg md:text-2xl text-gray-800">התקדמות שבועית</h3>
                <div className="flex items-center gap-3 md:gap-4">
                  <button
                    onClick={handleNextWeek}
                    disabled={!canNavigateNext()}
                    className={`p-2 md:p-3 rounded-full transition-all ${
                      canNavigateNext()
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
                        : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    }`}
                    title="שבוע הבא"
                  >
                    <span className="text-xl md:text-2xl">→</span>
                  </button>
                  <span className="text-sm md:text-base font-semibold text-gray-700 min-w-[100px] md:min-w-[120px] text-center">
                    {formatWeekRange(selectedWeekStart)}
                  </span>
                  <button
                    onClick={handlePreviousWeek}
                    className="p-2 md:p-3 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all cursor-pointer"
                    title="שבוע קודם"
                  >
                    <span className="text-xl md:text-2xl">←</span>
                  </button>
                </div>
              </div>
              
              {/* Filter Buttons */}
              <div className="mb-6 overflow-x-auto">
                <div className="flex gap-2 md:gap-3 pb-2" style={{ direction: 'rtl' }}>
                  <button
                    onClick={() => setSelectedDrillFilter('all')}
                    className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold text-sm md:text-base whitespace-nowrap transition-all flex-shrink-0 ${
                      selectedDrillFilter === 'all'
                        ? 'bg-gradient-to-r from-[#4F7BFF] to-[#6366F1] text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    הכל
                  </button>
                  {uniqueDrills.map((drill, idx) => {
                    const drillColor = getDrillColor(idx);
                    const isSelected = selectedDrillFilter === drill.drillId;
                    return (
                      <button
                        key={drill.drillId}
                        onClick={() => setSelectedDrillFilter(drill.drillId)}
                        className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold text-sm md:text-base whitespace-nowrap transition-all flex-shrink-0 ${
                          isSelected
                            ? 'text-white shadow-md'
                            : 'bg-white border-2 hover:opacity-80'
                        }`}
                        style={isSelected 
                          ? { backgroundColor: drillColor } 
                          : { borderColor: drillColor, color: drillColor }
                        }
                      >
                        {drill.drillName}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Bar Chart */}
              <div className="w-full -mx-2 md:-mx-4" style={{ direction: 'ltr' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={filteredChartData}
                    margin={{ top: 5, right: 0, left: -10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="day" 
                      tick={{ fill: '#6b7280', fontSize: 14 }}
                    />
                    <YAxis 
                      width={30}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px',
                        direction: 'rtl'
                      }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        
                        // Filter out entries with 0 minutes
                        const filteredPayload = payload.filter(item => {
                          const value = typeof item.value === 'number' ? item.value : 0;
                          return value > 0;
                        });
                        
                        if (filteredPayload.length === 0) return null;
                        
                        return (
                          <div style={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px',
                            padding: '12px',
                            direction: 'rtl'
                          }}>
                            <p style={{ color: '#374151', fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
                            {filteredPayload.map((entry, index) => {
                              let drillName = '';
                              if (selectedDrillFilter === 'all') {
                                const drill = uniqueDrills.find(d => d.drillId === entry.dataKey);
                                drillName = drill ? drill.drillName : entry.dataKey;
                              } else {
                                const drill = uniqueDrills.find(d => d.drillId === selectedDrillFilter);
                                drillName = drill ? drill.drillName : '';
                              }
                              const value = typeof entry.value === 'number' ? entry.value : 0;
                              return (
                                <p key={index} style={{ color: entry.color, margin: '4px 0' }}>
                                  {drillName}: {value} דקות
                                </p>
                              );
                            })}
                          </div>
                        );
                      }}
                      labelStyle={{ color: '#374151', fontWeight: 'bold' }}
                    />
                    {selectedDrillFilter === 'all' ? (
                      // Show multiple bars for each drill when "all" is selected (stacked)
                      uniqueDrills.map((drill, idx) => (
                        <Bar 
                          key={drill.drillId}
                          dataKey={drill.drillId} 
                          fill={getDrillColor(idx)}
                          radius={idx === uniqueDrills.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0]}
                          stackId="a"
                        />
                      ))
                    ) : (
                      // Show single bar with drill-specific color
                      <Bar 
                        dataKey="minutes" 
                        fill={getDrillColorById(selectedDrillFilter)}
                        radius={[8, 8, 0, 0]}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl animate-slide-up">
              <h3 className="font-bold mb-4 md:mb-6 text-lg md:text-2xl text-gray-800">{t('progress.recent_trainings')}</h3>
              {recentTrainings.length === 0 ? (
                <p className="text-gray-500 text-center py-8 md:py-12 text-base md:text-xl">{t('progress.no_recent_trainings')}</p>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {recentTrainings.slice(0, 10).map((training, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => handleSessionClick(training)}
                      className="border-b border-gray-200 pb-4 last:border-b-0 hover:bg-gray-50 p-3 md:p-4 rounded-xl transition-colors cursor-pointer"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-base md:text-xl text-gray-800">
                            {format(parseISO(training.startedAt), 'MMM dd, yyyy')}
                          </div>
                          <div className="text-gray-600 font-medium mt-1 text-sm md:text-base">
                            {training.drills.length} {t('progress.drills')} • {Math.floor(training.totalDurationSeconds / 60)} {t('training.minutes')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm md:text-base text-gray-500 font-medium">
                            {format(parseISO(training.startedAt), 'HH:mm')}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 md:mt-3 text-sm md:text-base text-gray-600">
                        {training.drills.map(drill => drill.drillName).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Drill Stats Tab */}
        {activeTab === 'drills' && (
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl animate-slide-up">
              <h3 className="font-bold mb-4 md:mb-6 text-lg md:text-2xl text-gray-800">{t('progress.most_practiced_drills')}</h3>
              {drillStats.length === 0 ? (
                <p className="text-gray-500 text-center py-8 md:py-12 text-base md:text-xl">{t('progress.no_drill_stats')}</p>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {drillStats.map((drill, idx) => (
                    <div key={drill.drillId} className="border-b border-gray-200 pb-4 last:border-b-0 hover:bg-gray-50 p-3 md:p-4 rounded-xl transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-base md:text-xl text-gray-800">{drill.drillName}</div>
                          <div className="text-gray-600 text-sm md:text-base font-medium mt-1">
                            {drill.sessions} {t('progress.sessions')} • {Math.floor(drill.totalTime / 60)} {t('training.minutes')}
                          </div>
                          {drill.lastPracticed && (
                            <div className="text-gray-500 text-xs md:text-sm mt-1">
                              {t('progress.last_practiced')}: {format(parseISO(drill.lastPracticed), 'MMM dd')}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                            #{idx + 1}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Session Details Modal */}
      <SessionDetailsModal
        isOpen={showSessionModal}
        session={selectedSession}
        onClose={() => {
          setShowSessionModal(false);
          setSelectedSession(null);
        }}
        onUpdate={handleSessionUpdate}
      />
    </div>
  );
}